/**
 * Từ điển nhãn cho mọi thứ liên quan tới tiền (CEO chốt 03/09/2026).
 *
 * Vì sao gom một chỗ: trước đây cùng một việc có ba tên gọi rải trên ba màn —
 * "Đối soát", "Kiểm soát", "Dò tiền về" — nên nhân sự không biết bấm cái nào.
 * Đổi chữ ở đây là đổi khắp nơi; thêm chữ mới thì bài kiểm cạnh bên bắt trùng.
 *
 * Nguyên tắc đặt tên: nói VIỆC người dùng đang làm, không nói TÊN KỸ THUẬT của
 * bảng dữ liệu. "Đối soát" là từ của kế toán; lễ tân không hiểu.
 */

import type { SalesOrderFlag } from "./api/salesOrderFlags";

/** Phương thức trả tiền — nâng từ bảng nhãn nội bộ của PaymentHistoryList. */
export const PAYMENT_METHOD_LABEL = {
  cash: "Tiền mặt",
  bank_transfer: "Chuyển khoản",
  card: "Thẻ",
  momo: "Momo",
  zalopay: "ZaloPay",
} as const;

/**
 * Mã phương thức → chữ người đọc ("bank_transfer" → "Chuyển khoản"); mã lạ trả
 * nguyên văn, trống trả chuỗi rỗng.
 *
 * Kiểm thử toàn vai 16/09/2026 (B6): thẻ Thanh toán của đơn, các tab Đã thu /
 * Phiếu chờ duyệt / Cần xem lại và bill in cho khách in thẳng "bank_transfer",
 * "cash" — chỉ PaymentHistoryList tra bảng nhãn.
 */
export function paymentMethodLabel(method: string | null | undefined): string {
  if (!method) return "";
  return PAYMENT_METHOD_LABEL[method as keyof typeof PAYMENT_METHOD_LABEL] ?? method;
}

/** Loại phiếu. */
export const PAYMENT_TYPE_LABEL = {
  deposit: "Cọc",
  partial: "Một phần",
  full: "Đủ",
  refund: "Hoàn",
} as const;

/**
 * Tình trạng phiếu. `pending` tách LÀM ĐÔI có chủ đích: cùng là "chờ", nhưng
 * một cái chờ KHÁCH bấm chuyển, cái kia chờ KẾ TOÁN bấm duyệt. Gộp một chữ
 * "Đang chờ" là hai người cùng ngồi đợi nhau.
 */
export const PAYMENT_STATUS_LABEL = {
  pendingWithQR: "Đang chờ khách chuyển",
  pendingNoQR: "Chờ kế toán duyệt",
  confirmed: "Đã thu",
  rejected: "Đã từ chối",
} as const;

/** Mức khớp của giao dịch ngân hàng. C và X cùng nghĩa với người dùng. */
export const MATCH_LEVEL_LABEL = {
  Q: "Khách quét QR",
  A: "Đúng cú pháp",
  B: "Máy gợi ý",
  C: "Chưa rõ đơn",
  X: "Chưa rõ đơn",
} as const;

/** Kiểu mã QR. */
export const QR_MODE_LABEL = {
  counter: "Tại quầy (15 phút)",
  remote: "Từ xa",
} as const;

/** Nút và chữ trong hộp Thu tiền. */
export const MONEY_LABEL = {
  collect: "💰 Thu tiền",
  /**
   * Lý do khoá nút thu của đơn ĐÃ HUỶ — một câu cho trang Thanh toán, trang đơn
   * (máy tính + điện thoại). Máy chủ cũng từ chối phiếu thu mới cho đơn huỷ
   * (kiểm thử đóng vai 17/10/2026, T02).
   */
  cancelledNoCollect: "Đơn đã huỷ — không thu thêm. Phiếu còn treo thì bấm Hủy chờ.",
  // Nhãn thẻ phải NGẮN: ba thẻ xếp một hàng, điện thoại 390px chỉ chứa ~356px.
  // Nhãn dài ("Gửi khách cách trả tiền" · "Ghi tiền mặt đã nhận") đẩy thẻ
  // "Tiền về chưa?" khuất hẳn khỏi mép phải — nhân sự tưởng nút đã bị gỡ
  // (Linh báo 17/10/2026, CEO chốt 1a).
  qrTab: "Gửi cách trả",
  cashTab: "Tiền mặt",
  lookupTab: "Tiền về chưa?",
  copyBundle: "Chép mã + nội dung",
  savePng: "Lưu ảnh mã QR",
  reuseQR: "Dùng lại mã đang chờ",
  moneyIn: "💸 Tiền về",
  askAccounting: "Nhờ kế toán xác nhận",
  dismissed: "Đã bỏ qua",
} as const;

/**
 * Chín tab của trang Thanh toán, ĐÚNG THỨ TỰ hiển thị.
 * 06/09/2026 thêm "Cần xem lại" (kiểm toán EXT-PAY-01, CEO chốt 2b): phiếu ĐÃ duyệt
 * còn mang cờ khách chuyển sai nội dung, chờ kế toán dò lại với sao kê.
 * Đối chiếu tên cũ → mới:
 *   Thu tiền            → Cần thu
 *   Kiểm soát           → tách: Đang chờ khách chuyển (phần QR) + Phiếu chờ duyệt (phần ghi tay)
 *   Lịch sử + Đã cọc    → gộp: Đã thu
 *   Đối soát            → tách: Tiền về chưa rõ đơn (việc phải làm) + Sổ ngân hàng (tra cứu)
 */
export const TAB_LABEL = {
  due: "Cần thu",
  waiting: "Phiếu chờ khách chuyển",
  collected: "Đã thu",
  queue: "Tiền về chưa rõ đơn",
  approve: "Phiếu chờ duyệt",
  review: "Cần xem lại",
  bank: "Sổ ngân hàng",
  accounting: "Cần xử lý bên Kế toán",
  tips: "Tiền tip",
  config: "Cấu hình",
} as const;

/**
 * Việc cần kế toán làm tay bên Kế toán, theo loại (QĐ 0311 Câu 11a). Nói VIỆC
 * phải làm, không nói tên kỹ thuật của dòng. `Record` theo đúng enum của hợp
 * đồng API: máy chủ thêm loại mới mà quên ở đây là cổng kiểu đỏ.
 */
export const SALES_ORDER_FLAG_LABEL: Record<SalesOrderFlag["kind"], string> = {
  cancelled_after_sent: "Đơn đã huỷ sau khi gửi Kế toán — huỷ đơn bên Kế toán",
  refund_after_milestone: "Đã hoàn tiền cho khách sau khi ghi doanh thu — ghi giảm bên Kế toán",
  decrease_after_milestone: "Tiền gói hoặc phát sinh giảm sau khi ghi doanh thu — ghi giảm bên Kế toán",
  extra_removed_after_sent: "Khoản phát sinh đã báo nay bị huỷ — ghi giảm bên Kế toán",
  rejected: "Tin gửi Kế toán bị từ chối — kiểm tra lý do rồi nhập tay bên Kế toán",
};
